import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Lightweight type-ahead combobox: text input + filtered dropdown.
 * - Always allows custom values (mirrors MS Access combo boxes).
 * - `options` is a flat string[].
 * - Optional `hint` shown next to the option (e.g. city for a suburb).
 *   Pass a function (option) => string|null.
 *
 * Usage:
 *   <Combobox value={form.address_city} onChange={v => set('address_city', v)} options={cities} placeholder="Hamilton" testId="client-city-combobox" />
 */
export default function Combobox({ value, onChange, options = [], placeholder = '', testId, hint, allowCustom = true, className = '', onAddNew, addLabel = 'Add' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [adding, setAdding] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(o => String(o).toLowerCase().includes(q))
    : options;
  // Cap rendered list to keep dropdown snappy
  const view = filtered.slice(0, 50);

  const commit = (v) => {
    onChange(v);
    setQuery(v);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, view.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (view[highlight]) commit(view[highlight]);
      else if (allowCustom) commit(query);
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} data-testid={testId} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setHighlight(0); }}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
            if (allowCustom) onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          className="w-full px-3 py-2 pr-8 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button type="button" onClick={() => setOpen(o => !o)} tabIndex={-1} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
          <ChevronDown size={14} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        </button>
      </div>
      {open && (
        <div ref={listRef} className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-[#E2E2DF] rounded-md shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">Loading options…</div>
          ) : view.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">
              {allowCustom ? `No matches — press Enter to use "${query}"` : 'No matches'}
            </div>
          ) : (
            view.map((opt, i) => {
              const isSelected = value === opt;
              const h = hint ? hint(opt) : null;
              return (
                <button
                  key={opt}
                  type="button"
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => commit(opt)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between ${i === highlight ? 'bg-[#FFF7ED] text-[#A04000]' : 'text-slate-700 hover:bg-slate-50'}`}
                  data-testid={testId ? `${testId}-opt-${opt}` : undefined}
                >
                  <span className="flex-1 truncate">{opt}</span>
                  {h && <span className="text-[10px] uppercase tracking-wider text-slate-400 ml-2">{h}</span>}
                  {isSelected && <Check size={12} className="text-[#D35400] ml-2" />}
                </button>
              );
            })
          )}
          {/* "+ Add" affordance — appears when caller passed onAddNew AND the typed value isn't an exact match */}
          {onAddNew && query.trim() && !options.some(o => o.toLowerCase() === query.trim().toLowerCase()) && (
            <button
              type="button"
              data-testid={testId ? `${testId}-add-new` : undefined}
              disabled={adding}
              onClick={async () => {
                setAdding(true);
                try {
                  const v = query.trim();
                  await onAddNew(v);
                  commit(v);
                } finally { setAdding(false); }
              }}
              className="w-full text-left px-3 py-2 text-sm font-semibold text-[#D35400] bg-[#FFF7ED] hover:bg-[#FFE7D2] border-t border-[#E2E2DF] flex items-center gap-1.5 disabled:opacity-50"
            >
              <span className="text-base leading-none">+</span>
              <span>{adding ? 'Adding…' : `${addLabel} "${query.trim()}"`}</span>
            </button>
          )}
          {filtered.length > view.length && (
            <div className="px-3 py-1.5 text-[10px] text-slate-400 italic border-t border-slate-100">Showing {view.length} of {filtered.length} — keep typing to narrow</div>
          )}
        </div>
      )}
    </div>
  );
}
